import {
    CreatorDownloadSource,
    DownloadableItem,
    DownloadableItemSource,
    Pack,
    RePack,
    Sample,
    Collection,
} from '../types';
import { EventIds, MultiDownloadId } from '../constants';
import { DownloadType, PackType } from '../types/enums';
import { log } from '../utils/log';

/**
 *
 * @param item - could be a Sample, Pack, RePack or an array of Sample IDs for multi-downloads
 * @return DownloadableItem - an item ready to be downloaded
 */
export const getDownloadableItem = (
    item: DownloadableItemSource,
): DownloadableItem => {
    if (Array.isArray(item)) {
        if (item.length === 1) {
            // If a multi-selection contains a single Sample, don't follow the Multi flow.
            // Only occurs in the FloatingActionsMenu, so isDownloaded is always false.
            return {
                type: DownloadType.Sample,
                id: item[0],
                itemIds: item,
                isDownloaded: false,
                isPremium: false,
            };
        }

        return {
            type: DownloadType.Multi,
            id: MultiDownloadId,
            itemIds: item,
            isDownloaded: false,
            isPremium: false,
        };
    }

    if ((item as Pack).type === PackType.Pack) {
        const pack = item as Pack;

        return {
            type: DownloadType.Pack,
            id: pack.guid,
            itemIds: [pack.guid],
            isDownloaded: item.isDownloaded,
            isFree: pack.isFree,
            artUrl: pack.artworkUrl,
            isPremium: !!pack?.premiumCodes?.length,
            name: pack.name,
        };
    }

    if ((item as RePack).type === PackType.RePack) {
        const repack = item as RePack;

        return {
            type: DownloadType.Repack,
            id: repack.guid,
            itemIds: [repack.guid],
            isDownloaded: item.isDownloaded,
            isFree: repack.isFree,
            artUrl: repack.artworkUrl,
            isPremium: !!repack?.premiumCodes?.length,
            name: repack.name,
        };
    }

    if (
        (item as CreatorDownloadSource).type === DownloadType.Creator ||
        (item as CreatorDownloadSource).type === DownloadType.CreatorMulti ||
        (item as CreatorDownloadSource).type === DownloadType.CreatorMixdown
    ) {
        item = item as CreatorDownloadSource;

        const { type } = item;
        const isMixdown = type === DownloadType.CreatorMixdown;

        return {
            type: item.type,
            id:
                item.samples.length > 1 && !isMixdown
                    ? MultiDownloadId
                    : item.samples[0].guid,
            itemIds: item.samples.map((sample) => sample.guid),
            samples: item.samples.map((sample) => ({
                id: sample.guid,
                gainDb: sample.gainDb,
                semitoneShift: sample.semitoneShift,
                timeFactor: sample.timeFactor,
            })),
            isDownloaded: item.isDownloaded,
            targetMeta: item.targetMeta,
            isPremium: false,
        };
    }

    if ((item as Collection).userId) {
        return {
            type:
                (item as Collection).sampleCount > 1
                    ? DownloadType.CollectionMulti
                    : DownloadType.Collection,
            id: (item as Collection).guid,
            itemIds: new Array((item as Collection).sampleCount).fill(
                (item as Collection).guid,
            ),
            isDownloaded: item.isDownloaded,
            isPremium: false,
        };
    }

    if ((item as Sample).guid && item.name) {
        const sample = item as Sample;

        return {
            type: DownloadType.Sample,
            id: sample.guid,
            itemIds: [sample.guid],
            isDownloaded: item.isDownloaded,
            isFiller: sample.isFiller,
            artUrl: sample.pack.artworkUrl,
            isPremium: false,
            name: sample.name,
        };
    }

    log.error(
        `Trying to generate a DownloadableItem from an invalid item`,
        EventIds.UnknownDownloadType,
        undefined,
        {
            item,
        },
    );

    return {
        type: DownloadType.Unknown,
        id: '',
        itemIds: [],
        isDownloaded: false,
        isPremium: false,
    };
};
